import React from "react";
import { Actions } from "./components/actions";
import { SpreadsheetTable } from "./components/table";
import { useController } from "./controllers";
import { DataImport } from "../data-import";
import { importData, exportData } from "@modules/spreadsheets/actions/spreadsheet";
import { MessageBox } from "@common/components/MessageBox";
import FormatManagement, { FORMAT_MANAGEMENT_MODE } from "@common/components/FormatManagement";
import { TreeGetIdArgs } from "@common/components/Tree/tree.types";
import { I18n } from "react-redux-i18n";
import { DataExport } from "../data-export";
import { NewInvite } from "@common/components/new-invite";
import { SpreadsheetManagement } from "../spreadsheet-management";

import './spreadsheet-result.styles.scss';
import {  SpreadsheetType } from "@modules/spreadsheets/dto/spreadsheet/type";
import {  SpreadsheetTableRunPayload } from "./components/table/table.types";

// Local props type (explicitly includes runPayload + optional temp id)
export type SpreadsheetResultProps = {
    spreadsheetType: SpreadsheetType;
    runPayload?: SpreadsheetTableRunPayload;
    isTemporaryRun?: boolean;
    spreadsheetId: number;
    onModeSwitch?: (mode: 'results' | 'management') => void;
    initialFolderId?: number;
    initialFamilyId?: number;
};

export const SpreadsheetResult: React.FC<SpreadsheetResultProps> = (props) => {
  const {
    spreadsheetType,
    runPayload,
    isTemporaryRun,
    spreadsheetId,
    onModeSwitch,
    initialFolderId,
    initialFamilyId,
  } = props;
  
  // Mode switching state: 'results' | 'management'
  const [currentMode, setCurrentMode] = React.useState<'results' | 'management'>('results');
  
  // Handle mode switching from parent
  const handleModeSwitch = React.useCallback((mode: 'results' | 'management') => {
    setCurrentMode(mode);
    if (onModeSwitch) {
      onModeSwitch(mode);
    }
  }, [onModeSwitch]);
  
  // Store table controller refs for export functionality
  const [tableControllerRefs, setTableControllerRefs] = React.useState<{
    queryDetailsRef: React.RefObject<any>;
    activeFilterRef: React.RefObject<any>;
  } | null>(null);
  
  // Add a refresh counter to force re-render when data changes
  // This is necessary because GWT bridge interference prevents normal React re-rendering
  const [dataRefreshCounter, setDataRefreshCounter] = React.useState(0);
  
  const tableKey = React.useMemo(() => {
    if (isTemporaryRun) {
      return `spreadsheet-table-temp-run-${dataRefreshCounter}`;
    }
    return `spreadsheet-table-${spreadsheetId || 'no-id'}-${dataRefreshCounter}`;
  }, [spreadsheetId, isTemporaryRun, dataRefreshCounter]);
  
  // Start in management mode when URL has ?mode=management
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const mode = params.get('mode');
      if (mode === 'management') {
        setCurrentMode('management');
      }
    } catch {}
  }, []);
  
  const {
    sendBatchNewInvites,
    currentSpreadsheetTypeConfig,
    messageProps,
    closeMessage,
    showErrorMessage,
    showInfoMessage,
    handleLoadFormat,
    handleSaveFormat,
    sendBatchInvitesController,
    dataImportController,
    formatManagementController,
    dataExportController,
    handleAddRow,
    handleDeleteRow,
    handleBatchLetters,
    isAddingRow,
    isDeletingRow,
    setSelectedRowData,
    setCurrentData,
    setSmartColumns,
    setDataResult,
    setTotalCount,
    setHasRrn,
    setHasParentField,
    setSelectedColumnIndex,
    setCanAddRow,
    setCanDeleteRow,
    canAddRow,
    canDeleteRow,
    currentData,
    smartColumns,
    totalCount,
    undoRedoState,
    trackCellEdit,
    trackRowAdd,
    trackRowDelete,
    trackBulkFill,
    undoRedoCallbacks,
    getCount,
    setClearSelectionEvent,
    clearSelectionEvent,
    handleEdit,tableController,isProcessingUndoRedo
  } = useController({ ...props, onModeSwitch: handleModeSwitch });
  

  React.useEffect(() => {
    // Only show the message if:
    // 1. totalCount is explicitly 0 (not null/undefined)
    // 2. We're not in a temporary run scenario (new spreadsheet)
    // 3. We have a valid spreadsheet ID (saved spreadsheet)
    if (typeof totalCount === 'number' && isFinite(totalCount) && totalCount === 0 && !isTemporaryRun && spreadsheetId > 0) {
      showInfoMessage(I18n.t('There are no matching rows for this query'));
    }
  }, [totalCount, showInfoMessage, isTemporaryRun, spreadsheetId]);


    // Render management mode or results mode based on current mode
    if (currentMode === 'management') {
      return (
        <SpreadsheetManagement
          spreadsheetType={spreadsheetType}
          propsSpreadsheetId={spreadsheetId}
          onModeSwitch={handleModeSwitch}
          initialFolderId={initialFolderId}
          initialFamilyId={initialFamilyId}
        />
      );
    }

    return (
        <>
            <MessageBox hideMessageBox={closeMessage} {...messageProps} />
            {sendBatchInvitesController.batchInvitesOpened && (
                <NewInvite
                    getQuestionnaires={sendBatchInvitesController.getQuestionnaires}
                    showErrorMessage={showErrorMessage}
                    getSteps={sendBatchInvitesController.getBatchNewInvitesSteps}
                    onSubmit={sendBatchNewInvites}
                    onClose={sendBatchInvitesController.closeBatchInvites}
                />
            )}
            {dataImportController.importDataOpened && (
                <DataImport
                    showErrorMessage={showErrorMessage}
                    close={dataImportController.closeImportData}
                    onImport={importData}
                    columnModel={smartColumns.length > 0 ? { smartColumns: smartColumns } : (runPayload?.columnModel || { smartColumns: [] })}
                    pss={spreadsheetType === SpreadsheetType.pedigree}
                    onImportComplete={() => {
                        // Force a complete reload when import completes successfully
                        // Increment counter to force table component recreation (bypasses GWT interference)
                        setDataRefreshCounter(prev => prev + 1);
                        
                        if (tableController.loadData) {
                            tableController.loadData(true, 0);
                        }
                    }}
                />
            )}
            {dataExportController.exportDataOpened && (
                <DataExport
                    showErrorMessage={showErrorMessage}
                    close={dataExportController.closeExportData}
                    onExport={async (exportParams: any) => {
                        // Get current spreadsheet data from the table controller
                        const scopedFolderId =
                            runPayload?.folderId ?? runPayload?.ssFormat?.folderId ?? initialFolderId ?? 0;
                        const scopedFamilyId =
                            spreadsheetType === SpreadsheetType.individual
                                ? (runPayload?.familyId ?? initialFamilyId ?? 0)
                                : 0;
                        const currentSpreadsheetData = {
                            columnModel: smartColumns.length > 0 ? { smartColumns: smartColumns } : null,
                            queryModel: tableControllerRefs?.queryDetailsRef?.current || { whereClauses: [] },
                            filter: tableControllerRefs?.activeFilterRef?.current || { whereClauses: [] },
                            pss: spreadsheetType === SpreadsheetType.pedigree,
                            folderId: scopedFolderId,
                            familyId: scopedFamilyId,
                            ...exportParams
                        };
                                                
                        if (!currentSpreadsheetData.columnModel) {
                            throw new Error('No spreadsheet data available for export');
                        }
                        
                        return await exportData(currentSpreadsheetData);
                    }}
                />
            )}
            {formatManagementController.loadFormatOpened && (
                <FormatManagement
                    showFormatNameHeader={false}
                    title={currentSpreadsheetTypeConfig.loadFmtTitle}
                    getId={{
                        modalTitle: 'fp-datasheet-format-folder-picker-panel',
                        creatingFolderName: 'tx-datasheet-add-folder-wnd-folder-name',
                        creatingFolderCancelBtn: 'lbl-datasheet-add-folder-wnd-cancel',
                        creatingFolderCreateBtn: 'lbl-datasheet-add-folder-wnd-save',
                        field: ({ rowIndex }: { rowIndex: number; colIndex: number }) => `bsgrid-${rowIndex}-0`,
                        creatingFolderItem: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}`
                            : `folder-child-${data.id}`,
                        creatingFolderCollapseItem: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}-fld-icon`
                            : `folder-child-${ownIndex}-fld-icon`,
                        folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}`
                            : `folder-child-${data.id}`,
                        collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}-fld-icon`
                            : `folder-child-${ownIndex}-fld-icon`,
                        newFolderBtn: 'btn-datasheet-format-folder-picker-new-folder',
                        deleteFolderBtn: 'btn-datasheet-format-folder-picker-delete-folder',
                        deleteFormatBtn: 'btn-datasheet-format-folder-picker-delete-format',
                        cancelBtn: 'btn-datasheet-format-folder-picker-cancel',
                        submitBtn: 'btn-datasheet-format-folder-picker-ok',
                    }}
                    onFormatDelete={formatManagementController.handleFormatDelete}
                    onFolderCreate={formatManagementController.handleFolderCreate}
                    onFolderDelete={formatManagementController.handleFolderDelete}
                    resetFormats={formatManagementController.handleFormatsReset}
                    folders={formatManagementController.folders}
                    formats={formatManagementController.formats}
                    getFolders={formatManagementController.getAndSetFolders}
                    getFormats={formatManagementController.getAndSetFormats}
                    close={formatManagementController.closeLoadFormat}
                    onSubmit={handleLoadFormat}
                />
            )}
            {formatManagementController.saveFormatOpened && (
                <FormatManagement
                    formatNameInputLabel={I18n.t('Name')}
                    showDescriptionInput={true}
                    showFormatNameHeader={false}
                    mode={FORMAT_MANAGEMENT_MODE.save}
                    title={currentSpreadsheetTypeConfig.saveFmtTitle}
                    initialFormatName={
                        (tableController as any)?.formatDetailsRef?.current?.name ||
                        (runPayload as any)?.ssFormat?.name ||
                        ''
                    }
                    initialFormatDescription={
                        (tableController as any)?.formatDetailsRef?.current?.description ||
                        (runPayload as any)?.ssFormat?.description ||
                        ''
                    }
                    getId={{
                        modalTitle: 'fp-datasheet-format-folder-picker-panel',
                        creatingFolderName: 'tx-datasheet-add-folder-wnd-folder-name',
                        creatingFolderCancelBtn: 'lbl-datasheet-add-folder-wnd-cancel',
                        creatingFolderCreateBtn: 'lbl-datasheet-add-folder-wnd-save',
                        field: ({ rowIndex }: { rowIndex: number; colIndex: number }) => `bsgrid-${rowIndex}-0`,
                        creatingFolderItem: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}`
                            : `folder-child-${data.id}`,
                        creatingFolderCollapseItem: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}-fld-icon`
                            : `folder-child-${ownIndex}-fld-icon`,
                        folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}`
                            : `folder-child-${data.id}`,
                        collapseIcon: ({ parentIndex, ownIndex }: TreeGetIdArgs) => parentIndex === undefined
                            ? `folder-${ownIndex}-fld-icon`
                            : `folder-child-${ownIndex}-fld-icon`,
                        newFolderBtn: 'btn-datasheet-format-folder-picker-new-folder',
                        deleteFolderBtn: 'btn-datasheet-format-folder-picker-delete-folder',
                        deleteFormatBtn: 'btn-datasheet-format-folder-picker-delete-format',
                        cancelBtn: 'btn-datasheet-format-folder-picker-cancel',
                        submitBtn: 'btn-datasheet-format-folder-picker-ok',
                    }}
                    onFormatDelete={formatManagementController.handleFormatDelete}
                    onFolderCreate={formatManagementController.handleFolderCreate}
                    onFolderDelete={formatManagementController.handleFolderDelete}
                    resetFormats={formatManagementController.handleFormatsReset}
                    folders={formatManagementController.folders}
                    formats={formatManagementController.formats}
                    getFolders={formatManagementController.getAndSetFolders}
                    getFormats={formatManagementController.getAndSetFormats}
                    close={formatManagementController.closeSaveFormat}
                    onSubmit={handleSaveFormat}
                />
            )}
            <div className="SpreadsheetDetailsSpreadsheetResult">
                <Actions
                    className="SpreadsheetDetailsSpreadsheetResult__actions"
                    disabledState={{ 
                        undo: !undoRedoState.canUndo, 
                        redo: !undoRedoState.canRedo, 
                        export: false 
                    }}
                    spreadsheetType={spreadsheetType}
                    onLoadFormat={formatManagementController.openLoadFormat}
                    onSaveFmt={formatManagementController.openSaveFormat}
                    onEdit={handleEdit}
                    onExport={dataExportController.openExportData}
                    onImport={dataImportController.openImportData}
                    onBatchLetters={handleBatchLetters}
                    onBatchInvites={sendBatchInvitesController.openBatchInvites}
                    onCount={async () => {
                      try {
                        const count = await getCount();
                        const displayCount = isFinite(count) ? count : 0;
                        showInfoMessage(`There are ${displayCount} main row(s) in this spreadsheet`);
                        if (isFinite(count)) {
                          setTotalCount(count);
                        }
                      } catch (err: any) {
                        showErrorMessage(err?.entity || err?.message || I18n.t('Failed to fetch count'));
                      }
                    }}
                    onUndo={undoRedoState.onUndo || (() => {})}
                    onRedo={undoRedoState.onRedo || (() => {})}
                    onAddRow={handleAddRow}
                    onDeleteRow={handleDeleteRow}
                    canAddRow={canAddRow}
                    canDeleteRow={canDeleteRow}
                    isAddingRow={isAddingRow}
                    isDeletingRow={isDeletingRow}
                    isProcessingUndoRedo={isProcessingUndoRedo}
                />
                <SpreadsheetTable
                    spreadsheetType={spreadsheetType}
                    className="SpreadsheetDetailsSpreadsheetResult__table"
                    spreadsheetId={spreadsheetId || (isTemporaryRun ? 0 : spreadsheetId)}
                    runPayload={isTemporaryRun ? runPayload : undefined}
                    folderId={initialFolderId}
                    familyId={initialFamilyId}
                    getColumns={() => Promise.resolve([])}
                    onLoadData={() => Promise.resolve()}
                    key={tableKey}
                    onSelectedRowDataChange={setSelectedRowData}
                    onCurrentDataChange={setCurrentData}
                    onSmartColumnsChange={setSmartColumns}
                    onDataResultChange={setDataResult}
                    onTotalCountChange={setTotalCount}
                    onHasRrnChange={setHasRrn}
                    onHasParentFieldChange={setHasParentField}
                    onUndo={undoRedoState.onUndo || (() => {})}
                    onRedo={undoRedoState.onRedo || (() => {})}
                    onSelectedColumnIndexChange={setSelectedColumnIndex}
                    onCanAddRowChange={setCanAddRow}
                    onCanDeleteRowChange={setCanDeleteRow}
                    onAddRow={handleAddRow}
                    onDeleteRow={handleDeleteRow}
                    isAddingRow={isAddingRow}
                    parentData={currentData}
                    showErrorMessage={showErrorMessage}
                    showInfoMessage={showInfoMessage}
                    trackCellEdit={trackCellEdit}
                    trackRowAdd={trackRowAdd}
                    trackRowDelete={trackRowDelete}
                    trackBulkFill={trackBulkFill}
                    onBulkCellUpdate={undoRedoCallbacks.onBulkCellUpdate}
                    undo={undoRedoState.onUndo}
                    redo={undoRedoState.onRedo}
                    canUndo={undoRedoState.canUndo}
                    canRedo={undoRedoState.canRedo}
                    onControllerRefsReady={setTableControllerRefs}
                    onSetClearSelectionEvent={setClearSelectionEvent}
                    clearSelectionEvent={clearSelectionEvent}
                    isProcessingUndoRedo={isProcessingUndoRedo}
                    isDeletingRow={isDeletingRow}
                />
            </div>
        </>
    );
};